import { Button, Checkbox, FormControl, FormControlLabel, Grid } from "@mui/material";
import { AxiosError } from "axios";
import { RepeatWidget } from "components/shared/biz";
import { getRRuleFrequency, MONTHDAYS, WEEKDAYS } from "components/shared/biz/utils/rruleUtils";
import GroupedAssignee from "components/todolist/EditTaskDialog/GroupedAssignee";
import { useTimezones } from "components/todolist/hooks";
import { EMAILID_COOKIE, FEATURE_KEY, FIRST_NAME_COOKIE, LAST_NAME_COOKIE } from "constant";
import moment from "moment-timezone";
import { useAxios } from "myAxios";
import { AuthProvider } from "pages";
import { FC, useContext, useMemo, useReducer, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { toast } from "react-toastify";
import { RRule, rrulestr } from "rrule";
import { RepeatEntity, TodoItemObject } from "types/todolist";
import { Cookies } from "utils";
import Assignee from "./EditTaskDialog/Assignee";
import DueDate from "./EditTaskDialog/DueDate";
import TaskTitle from "./EditTaskDialog/TaskTitle";
import TimeZone from "./EditTaskDialog/TimeZones";
import Reminders, { ReminderType } from "components/todolist/Reminders";
import reminderReducer from "components/todolist/Reminders/hooks/reminderReducer";

interface EditTaskProps {
  type: "create" | "update";
  item?: TodoItemObject;
  onClose?: () => void;
  getOpenStatus?: (openStatus: boolean) => void;
  mutate?: any;
  mutateOverdue?: any;
}

export interface PostParams {
  userTimezone: string | null;
  content: string;
  assignedTo: string;
  dueDate: string | null;
  repeatMethod: string;
  rrule: string | null;
  endDate: string | null;
  completed?: boolean;
  itemId?: string | null;
  reminders?: Pick<ReminderType, "interval" | "intervalBy" | "reminderTime" | "template">[];
}

const EditTask: FC<EditTaskProps> = ({
  type,
  item,
  onClose,
  getOpenStatus,
  mutate,
  mutateOverdue,
}) => {
  const axios = useAxios();

  const { reset, control, handleSubmit, watch, setValue } = useForm();
  const [updating, setUpdating] = useState(false);
  const authDetail = useContext(AuthProvider);

  const { data: timezones } = useTimezones();
  const currentZone = item?.userTimezone || moment.tz.guess();
  const watchTimezone = watch("timezone") || currentZone;
  const rruleOptions = item?.rrule ? rrulestr(item?.rrule, { dtstart: new Date() }).options : null;

  const isSubTask = useMemo(() => {
    return !!(item?.itemId && item?.repeatMethod);
  }, [item]);
  const [reminderList, dispatch] = useReducer(reminderReducer, item?.reminders || []);

  const repeatEntity: RepeatEntity = {
    frequency: {
      name: "frequency",
      required: true,
      value: item?.repeatMethod || "weekly",
      disabled: type === "update" && !!item?.itemId && !!item?.repeatMethod,
    },
    interval: {
      name: "interval",
      required: true,
      value: rruleOptions?.interval || 1,
      disabled: type === "update" && !!item?.itemId && !!item?.repeatMethod,
    },
    weekdays: {
      name: "weekdays",
      required: false,
      value: WEEKDAYS.filter((day) =>
        (rruleOptions?.byweekday || []).some((wd: number) => String(wd + 1) === day.key)
      ),
      disabled: type === "update" && !!item?.itemId && !!item?.repeatMethod,
    },
    monthdays: {
      name: "monthdays",
      required: false,
      value: MONTHDAYS.filter((day) =>
        (rruleOptions?.bymonthday || []).some((md: number) => String(md) === day.key)
      ),
      disabled: type === "update" && !!item?.itemId && !!item?.repeatMethod,
    },
  };

  const userEmail = Cookies.get(EMAILID_COOKIE);
  const userName = Cookies.get(FIRST_NAME_COOKIE);
  const lastName = Cookies.get(LAST_NAME_COOKIE);
  const [isRecurring, setIsRecurring] = useState<boolean>(!!item?.repeatMethod);

  const onSubmit = async (submitData: { [key: string]: any }) => {
    let postData: Partial<PostParams> = {
      userTimezone: submitData.timezone,
      content: submitData.taskTitle,
      //assignedTo: submitData.assignee?.cecId ?? null,
      dueDate: submitData.dueDate?.length
        ? moment(submitData.dueDate?.[0])?.format("YYYY-MM-DD")
        : null,
      endDate: submitData.endDate?.length
        ? moment(submitData.endDate?.[0])?.format("YYYY-MM-DD")
        : null,
      reminders: reminderList.map((reminder) => ({
        interval: reminder.interval,
        intervalBy: reminder.intervalBy,
        reminderTime: reminder.reminderTime,
        template: reminder.template,
      })),
    };

    if (isRecurring) {
      const rruleOption: any = {
        freq: getRRuleFrequency(submitData.frequency),
        interval: submitData.interval || 1,
      };
      if (submitData.frequency === "weekly") {
        rruleOption.byweekday = (submitData.weekdays || []).map((day: any) => day.value);
      }
      if (submitData.frequency === "monthly") {
        rruleOption.bymonthday = (submitData.monthdays || []).map((day: any) => day.value);
      }
      const rrule = new RRule(rruleOption as any);
      postData = {
        ...postData,
        repeatMethod: submitData.frequency,
        rrule: rrule.toString(),
      };
    }

    if (type === "create") {
      postData = {
        ...postData,
        assignedTo: (submitData.assignee || []).map((a: any) => a.cecId).join(","),
      };
      try {
        await axios.post<string>(`/api/dataservice/v1/todos`, postData);
        mutate?.();
        mutateOverdue?.();
        onClose?.();
        reset();
        toast.success("Task saved", {
          position: "top-center",
          autoClose: 3000,
        });
      } catch (e) {
        setUpdating(false);
        toast.error((e as AxiosError)?.response?.data?.message || "Fail to create task", {
          position: "top-center",
          autoClose: 3000,
        });
      }
    } else {
      try {
        postData = {
          ...postData,
          completed: item?.completed,
          itemId: item?.itemId,
          assignedTo: submitData.assignee?.cecId ?? null,
        };
        if (item?.id) {
          await axios.put(`/api/dataservice/v1/todos/${item?.id}`, postData);
          mutate?.();
          mutateOverdue?.();
          onClose?.();
          getOpenStatus?.(false);
          toast.success("Update task successfully!", {
            position: "top-center",
            autoClose: 3000,
          });
        }
      } catch (e) {
        setUpdating(false);
        toast.error((e as AxiosError)?.response?.data?.message || "Fail to update task", {
          position: "top-center",
          autoClose: 3000,
        });
      }
    }
  };

  const onError = (errors: { [key: string]: any }) => {
    if (Object.keys(errors)?.length) {
      toast.error("Please fill in the highlighted field(s)", {
        position: "top-center",
        autoClose: 3000,
      });
    }
  };

  return (
    <form>
      <div
        style={{
          border: "1px solid #ddd",
          borderRadius: 8,
          padding: "16px 20px",
          marginBottom: type === "update" ? 20 : "auto",
        }}
      >
        <Grid style={{ marginBottom: 0 }}>
          <FormControl
            variant="outlined"
            size="small"
            fullWidth
          >
            <Controller
              name="taskTitle"
              control={control}
              rules={{
                required: {
                  message: "This field is required!",
                  value: true,
                },
              }}
              defaultValue={item?.content ?? ""}
              render={(props) => <TaskTitle {...props} />}
            />
          </FormControl>
        </Grid>

        <Grid
          container
          spacing={2}
          style={{ marginBottom: 10 }}
        >
          <Grid
            item
            xs={6}
            style={{ marginTop: -4 }}
          >
            <FormControl
              variant="outlined"
              size="small"
              fullWidth
            >
              <Controller
                name="dueDate"
                control={control}
                rules={{
                  required: false,
                }}
                defaultValue={
                  item?.dueDate
                    ? [moment.tz(item.dueDate, item.userTimezone).format("MM/DD/YYYY")]
                    : [moment().tz(currentZone).format("MM/DD/YYYY")]
                }
                render={(props) => (
                  <DueDate
                    {...props}
                    label={"Due Date"}
                    zone={watchTimezone}
                    disabled={isSubTask ? false : isRecurring}
                  />
                )}
              />
            </FormControl>
          </Grid>
          <Grid
            item
            xs={6}
          >
            {timezones && (
              <FormControl
                variant="outlined"
                size="small"
                fullWidth
              >
                <Controller
                  name="timezone"
                  control={control}
                  rules={{
                    required: {
                      message: "This field is required!",
                      value: true,
                    },
                  }}
                  defaultValue={currentZone}
                  render={(props) => (
                    <TimeZone
                      {...props}
                      opts={timezones}
                    />
                  )}
                />
              </FormControl>
            )}
          </Grid>
        </Grid>

        {authDetail?.features?.includes(FEATURE_KEY.EDIT_TO_DO_LIST_ASSIGN) && (
          <Grid
            container
            spacing={2}
            style={{ marginBottom: 10 }}
          >
            <Grid
              item
              xs={6}
            >
              <FormControl
                variant="outlined"
                size="small"
                fullWidth
              >
                {type === "create" ? (
                  <Controller
                    name="assignee"
                    control={control}
                    rules={{
                      required: false,
                    }}
                    defaultValue={[
                      {
                        cecId: userEmail?.split("@")?.[0],
                        name: `${userName} ${lastName}`,
                        group: "My Team",
                      },
                    ]}
                    render={(props) => <GroupedAssignee {...props} />}
                  />
                ) : (
                  <Controller
                    name="assignee"
                    control={control}
                    rules={{
                      required: false,
                    }}
                    defaultValue={
                      item
                        ? { cecId: item.assignedTo, name: item.assigneeName }
                        : {
                            cecId: userEmail?.split("@")?.[0],
                            name: `${userName} ${lastName}`,
                          }
                    }
                    render={(props) => <Assignee {...props} />}
                  />
                )}
              </FormControl>
            </Grid>
          </Grid>
        )}

        <Grid
          container
          spacing={2}
          style={{ marginBottom: 10 }}
        >
          <Grid
            item
            xs={6}
          >
            <FormControlLabel
              control={
                <Checkbox
                  checked={isRecurring}
                  onChange={(e) => setIsRecurring(e.target.checked)}
                  disabled={type === "update"}
                />
              }
              label="Recurring"
            />
          </Grid>
        </Grid>
        {isRecurring && (
          <Grid
            container
            spacing={2}
            style={{ marginBottom: 10 }}
          >
            <Grid
              item
              xs={6}
            >
              <RepeatWidget
                control={control}
                entity={{
                  ...repeatEntity,
                }}
                setValue={setValue}
              />
            </Grid>
          </Grid>
        )}
        {isRecurring && (
          <Grid
            container
            spacing={2}
            style={{ marginBottom: 10 }}
          >
            <Grid
              item
              xs={6}
            >
              <FormControl
                variant="outlined"
                size="small"
                fullWidth
              >
                <Controller
                  name="endDate"
                  control={control}
                  /* rules={{
                    required: {
                      message: "This field is required!",
                      value: true,
                    },
                  }} */
                  defaultValue={
                    item?.endDate
                      ? [moment.tz(item.endDate, item.userTimezone).format("MM/DD/YYYY")]
                      : null
                  }
                  render={(props) => (
                    <DueDate
                      {...props}
                      label={"End Date"}
                      disabled={isSubTask}
                    />
                  )}
                />
              </FormControl>
            </Grid>
          </Grid>
        )}
        <Grid
          container
          spacing={2}
          style={{ marginBottom: 10 }}
        >
          <Grid
            item
            xs={12}
          >
            <Reminders
              control={control}
              reminderList={reminderList}
              dispatch={dispatch}
            />
          </Grid>
        </Grid>

        <div style={{ display: "flex", justifyContent: "flex-end" }}>
          <Button
            variant="outlined"
            onClick={() => {
              onClose?.();
              getOpenStatus?.(false);
            }}
            sx={{ mr: 1 }}
            size="small"
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            sx={{
              backgroundColor: "#030213",
              color: "#fff",
              "&:hover": {
                backgroundColor: "rgba(3, 2, 19, 0.9)",
              },
              filter: updating ? "opacity(0.5)" : undefined,
              pointerEvents: updating ? "none" : "auto",
            }}
            onClick={handleSubmit(onSubmit, onError)}
            type="submit"
            size="small"
          >
            Save
          </Button>
        </div>
      </div>
    </form>
  );
};
export default EditTask;
